<template>
    <div class="py-4 flex flex-col gap-4">
        <div
            v-for="audio in props.audios"
            :key="audio.id"
            class="px-4 py-2 cursor-move"
            @dragstart="emit('audiodrag', $event, audio, 'audio')"
            @drop="emit('audiodrop', $event, 'audio')"
            @click="handleClick(audio)"
            draggable="true"
        >
            <div class="flex gap-2 items-center">
                <div
                    class="
                        bg-gray-600
                        flex
                        items-center
                        justify-center
                        text-white
                        p-3
                        text-[20px]
                    "
                >
                    <ion-icon name="play-circle"></ion-icon>
                </div>
                <div class="text-bold text-[15px]">{{ audio.title }}</div>
            </div>
        </div>
        <div
            class="
                bg-primary
                rounded-lg
                mx-4
                py-2
                px-3
                text-white text-md
                flex
                items-center
                gap-1
                w-fit
            "
            @click="state.isOpen = true"
        >
            <ion-icon name="add-outline"></ion-icon>
            Upload audio
        </div>
        <MiniModalCustom title="Upload audio" :is-open="state.isOpen">
            <hr class="gray-500 my-4" />

            <div
                v-if="state.audio.isUploading"
                class="flex flex-col items-center justify-center"
            >
                <LoadingCircle></LoadingCircle>
                <div class="mt-3 text-[16px] text-bold">
                    {{ state.audio.progress }}
                </div>
            </div>
            <form method="post" v-else>
                <div class="my-2">
                    <label for="title" class="block font-semibold my-2 text-sm">
                        Title
                    </label>
                    <new-input
                        required
                        maxlength="128"
                        label="Title"
                        name="title"
                        placeholder="Enter audio title"
                        v-model="state.audio.title"
                    ></new-input>
                </div>

                <div class="my-2">
                    <label
                        for="description"
                        class="block font-semibold my-2 text-sm"
                    >
                        Description
                    </label>
                    <new-input
                        maxlength="128"
                        label="Description"
                        name="description"
                        placeholder="Enter audio description"
                        v-model="state.audio.description"
                    ></new-input>
                </div>

                <input
                    name="audio"
                    type="file"
                    @change="selectFile()"
                    ref="inputFileRef"
                />

                <div class="mt-8 flex items-center gap-4 justify-end">
                    <app-button
                        @click="state.isOpen = false"
                        class="border border-accent text-accent !bg-white"
                    >
                        Cancel
                    </app-button>
                    <app-button
                        :disabled="state.file === null"
                        type="button"
                        class="bg-primary text-white"
                        @click="uploadAudio()"
                        >Submit</app-button
                    >
                </div>
            </form>
        </MiniModalCustom>
    </div>
</template>

<script setup>
import { defineEmits, reactive, onMounted, defineProps, ref } from "vue";

const emit = defineEmits(["updated", "audiodrag", "audiodrop", "dblclick"]);

const props = defineProps({
    audios: {
        type: Array,
    },
});

const state = reactive({
    isOpen: false,
    clickTimeoutId: null,
    audio: {
        title: "",
        description: "",
        file: null,
        thumbnail: null,
        progress: "Please wait, uploading 0%",
        isUploading: false,
        uploadId: null,
        parts: [],
    },
});

const teamIdFromUrl = new URLSearchParams(window.location.search).get("teamId");

const inputFileRef = ref(null);

async function getData() {
    const teamId = teamIdFromUrl ? teamIdFromUrl : "";
    const resp = await axios.get("/audio/list?teamId=" + teamId);
    emit("updated", resp.data);
}

function handleClick(audio) {
    if (!state.clickTimeoutId) {
        state.clickTimeoutId = setTimeout(() => {}, 50);
    } else {
        clearTimeout(state.clickTimeoutId);
        emit("dblclick", audio);
    }
}

onMounted(() => {
    getData();
});

function selectFile() {
    if (inputFileRef.value.files.length === 0) {
        return;
    }
    const file = inputFileRef.value.files[0];
    state.audio.file = file;
}

async function uploadAudio() {
    try {
        state.audio.isUploading = true;
        let dir = teamIdFromUrl;

        const key = state.audio.file.name;
        const createResponse = await axios.post(
            "/upload/create-multipart-upload",
            { key, dir }
        );
        state.audio.uploadId = createResponse.data.UploadId;

        await uploadParts(createResponse.data.Key);
        const resp = await completeUpload(createResponse.data.Key);
        await createAudioEntry(resp.data.url);
    } catch (e) {
        console.error(e);
    }

    state.audio.isUploading = false;
    state.audio.parts = [];
}

async function uploadParts(key) {
    const fileSize = state.audio.file.size;
    let start = 0;
    let partNumber = 1;
    const partSize = 100 * 1024 * 1024; // 100MB part size
    const partLength = Math.round(state.audio.file.size / partSize);
    let partIndex = 0;

    while (start < fileSize) {
        const end = Math.min(start + partSize, fileSize);
        const filePart = state.audio.file.slice(start, end);

        // Get presigned URL from the server
        const presignedResponse = await axios.post(
            "/upload/generate-presigned-url",
            {
                key: key,
                uploadId: state.audio.uploadId,
                partNumber,
            }
        );

        let presignedUrl = presignedResponse.data.url;
        presignedUrl = presignedUrl.replace(
            "http://host.docker.internal:4566",
            "http://localhost:4566"
        );

        // Upload the file part to S3
        const uploadResponse = await axios.put(presignedUrl, filePart, {
            headers: { "Content-Type": "application/octet-stream" },
        });

        state.audio.parts.push({
            PartNumber: partNumber,
            ETag: uploadResponse.headers.etag,
        });

        start = end;
        partNumber++;
        partIndex++;
        const progress = Math.round((partIndex / partLength) * 100);
        if (progress < 99) {
            state.audio.progress = `Please wait, uploading ${progress}%`;
        } else {
            state.audio.progress = "Processing...";
        }
    }
}

async function completeUpload(key) {
    return axios.post("/upload/complete-multipart-upload", {
        key: key,
        uploadId: state.audio.uploadId,
        parts: state.audio.parts,
    });
}

async function createAudioEntry(url) {
    let formData = new FormData();
    formData.append("url", url);
    formData.append("size", state.audio.file.size);
    formData.append("title", state.audio.title);
    formData.append("description", state.audio.description);
    if (teamIdFromUrl) {
        formData.append("team_id", teamIdFromUrl);
    }

    try {
        await axios.post("/audio/store", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        getData();
        state.isOpen = false;
    } catch (e) {
        console.error(e);
    }
}
</script>
